import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Mail, Phone, ExternalLink, RefreshCw } from "lucide-react";
import { Mark } from "@/components/site/Logo";
import { Flag } from "@/components/site/Flags";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDetail = (d) => {
  if (d == null) return "Something went wrong. Try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (typeof e?.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof d?.msg === "string") return d.msg;
  return String(d);
};

const KIND_TONE = { client: "bg-[#FF4D1A] text-white", talent: "bg-[#2234F5] text-white", contact: "bg-[#D4FF3D] text-[#0E0E10]" };
const STATUS_TONE = { new: "bg-[#0E0E10] text-[#F1ECE2]", replied: "bg-[#D4FF3D] text-[#0E0E10]", archived: "bg-black/10 text-[#57534B]" };

const Action = ({ onClick, testid, children, muted }) => (
  <button onClick={onClick} data-testid={testid} className={`ul-draw text-sm font-semibold ${muted ? "text-[#57534B]" : ""}`}>{children}</button>
);

const Card = ({ e, onStatus }) => (
  <motion.div layout initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
    className={`border-2 p-5 sm:p-6 ${e.status === "archived" ? "border-black/10 bg-[#f4f0e8] opacity-60" : e.status === "replied" ? "border-[#0E0E10]/40 bg-[#F1ECE2]" : "border-[#0E0E10] bg-[#F1ECE2] shadow-[6px_6px_0_#0E0E10]"}`} data-testid={`enquiry-card-${e.id}`}>
    <div className="flex flex-wrap items-center gap-3">
      <span className={`font-mono-x rounded-full px-3 py-1 text-xs font-bold ${KIND_TONE[e.kind]}`}>{e.kind}</span>
      <span className={`font-mono-x rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] ${STATUS_TONE[e.status]}`} data-testid={`status-${e.id}`}>{e.status}</span>
      <Flag id={e.region} className="w-5" />
      <span className="font-mono-x text-xs text-[#57534B]">{e.created_at} UTC · via {e.wing}</span>
      {e.email_status !== "sent" && <span className="font-mono-x rounded-full bg-black/10 px-2 py-0.5 text-xs">email {e.email_status}</span>}
      <span className="ml-auto flex gap-4">
        {e.status === "new" && <Action onClick={() => onStatus(e.id, "replied")} testid={`mark-replied-${e.id}`}>mark replied</Action>}
        {e.status !== "archived" && <Action onClick={() => onStatus(e.id, "archived")} testid={`archive-${e.id}`} muted>archive</Action>}
        {e.status !== "new" && <Action onClick={() => onStatus(e.id, "new")} testid={`reopen-${e.id}`} muted>reopen</Action>}
      </span>
    </div>
    <p className="font-display mt-3 text-2xl font-bold tracking-tight">{e.name} <span className="text-[#57534B]">· {e.role || e.company || "—"}</span></p>
    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
      <a href={`mailto:${e.email}`} className="ul-draw inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{e.email}</a>
      {e.phone && <a href={`tel:${e.phone}`} className="ul-draw inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{e.phone}</a>}
      {e.profile_url && <a href={e.profile_url} target="_blank" rel="noreferrer" className="ul-draw inline-flex items-center gap-1.5"><ExternalLink className="h-3.5 w-3.5" />profile</a>}
    </div>
    {e.message && <p className="mt-3 max-w-2xl whitespace-pre-line leading-relaxed text-[#3d3a35]">{e.message}</p>}
    <p className="font-mono-x mt-3 text-xs text-[#57534B]">{e.experience || ""}{e.experience && e.company ? " · " : ""}{e.company || ""}</p>
  </motion.div>
);

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
      onLogin(data);
    } catch (e) {
      toast.error(fmtDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex min-h-[80vh] items-center justify-center px-5 pt-24">
      <form onSubmit={submit} className="w-full max-w-sm border-2 border-[#0E0E10] bg-[#F1ECE2] p-8 shadow-[10px_10px_0_#FF4D1A]" data-testid="admin-login-form">
        <Mark className="h-12 w-12" />
        <h1 className="font-display mt-4 text-3xl font-bold tracking-tight">team only.</h1>
        <p className="font-mono-x mt-1 text-xs text-[#57534B]">the desk where every enquiry lands</p>
        <label className="mt-8 block"><span className="font-mono-x text-xs uppercase tracking-[0.2em]">email</span>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" className="field" /></label>
        <label className="mt-5 block"><span className="font-mono-x text-xs uppercase tracking-[0.2em]">password</span>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" className="field" /></label>
        <button type="submit" disabled={busy} data-testid="admin-login-btn" className="font-display mt-8 w-full bg-[#0E0E10] py-3.5 font-bold text-[#F1ECE2] transition-colors hover:bg-[#FF4D1A]">
          {busy ? "checking…" : "let me in"}
        </button>
      </form>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("open");

  useEffect(() => {
    document.title = "Team only";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then((r) => { setUser(r.data); })
      .catch(() => { setUser(false); })
      .finally(() => setChecking(false));
  }, []);

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/admin/enquiries`, { withCredentials: true });
      setData(d);
    } catch (e) {
      if (e.response?.status === 401) { setUser(false); }
      else toast.error(fmtDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => { if (user) load(); }, [user]);

  const setStatus = async (id, status) => {
    setData((d) => ({ ...d, items: d.items.map((i) => (i.id === id ? { ...i, status } : i)) }));
    try {
      await axios.patch(`${API}/api/admin/enquiries/${id}`, { status }, { withCredentials: true });
    } catch (e) {
      toast.error(fmtDetail(e.response?.data?.detail));
      load();
    }
  };

  const logout = async () => {
    await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(false);
  };

  const shown = useMemo(() => (data?.items || []).filter((i) =>
    (filter === "all" || i.kind === filter) &&
    (statusFilter === "all" || (statusFilter === "open" ? i.status !== "archived" : i.status === statusFilter))
  ), [data, filter, statusFilter]);

  if (checking) return <div className="min-h-screen" />;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <div className="min-h-screen px-5 pb-24 pt-28 sm:px-8" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">the desk.</h1>
            <p className="font-mono-x mt-1 text-sm text-[#57534B]">signed in as {user.email}</p>
          </div>
          <div className="flex gap-3">
            <button onClick={load} data-testid="admin-refresh-btn" className="ul-draw inline-flex items-center gap-2 text-sm font-semibold"><RefreshCw className="h-4 w-4" />refresh</button>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 rounded-full bg-[#0E0E10] px-5 py-2.5 text-sm font-semibold text-[#F1ECE2] hover:bg-[#FF4D1A]"><LogOut className="h-4 w-4" />log out</button>
          </div>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {[["new", data?.stats.new], ["replied", data?.stats.replied], ["archived", data?.stats.archived], ["today", data?.stats.today], ["client", data?.stats.client], ["talent", data?.stats.talent]].map(([k, v]) => (
            <div key={k} className="border-2 border-[#0E0E10] p-4" data-testid={`stat-${k}`}>
              <p className="font-display text-4xl font-bold">{v ?? "–"}</p>
              <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-[#57534B]">{k}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="status">
            {[["open", "open"], ["new", "new"], ["replied", "replied"], ["archived", "archived"], ["all", "everything"]].map(([k, l]) => (
              <button key={k} onClick={() => setStatusFilter(k)} data-testid={`status-filter-${k}-btn`} role="tab" aria-selected={statusFilter === k}
                className={`rounded-full border-2 border-[#0E0E10] px-4 py-1.5 text-sm font-semibold transition-colors ${statusFilter === k ? "bg-[#FF4D1A] border-[#FF4D1A] text-white" : "hover:bg-black/5"}`}>{l}</button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="kind">
            {["all", "client", "talent", "contact"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}-btn`} role="tab" aria-selected={filter === f}
                className={`rounded-full border-2 border-[#0E0E10] px-4 py-1.5 text-sm font-semibold transition-colors ${filter === f ? "bg-[#0E0E10] text-[#F1ECE2]" : "hover:bg-black/5"}`}>{f}</button>
            ))}
          </div>
        </div>
        <div className="mt-6 grid gap-4">
          {shown.map((e) => <Card key={e.id} e={e} onStatus={setStatus} />)}
          {shown.length === 0 && <p className="font-mono-x py-16 text-center text-[#57534B]" data-testid="empty-state">nothing here. {statusFilter === "open" ? "the first brief starts the clock." : "try another filter."}</p>}
        </div>
      </div>
    </div>
  );
}
